import {
    SuiteId,
    TIMEZONE_KEY,
    CurrentMember,
    MemberType,
    CurrentTenant,
    CurrentUser,
    LoginInfo,
    NavigationConf,
    SuiteConf,
    CorpType,
    PlatformVip,
    PlatformVipAddOnKey,
    PlatformVipLevel,
    SuiteVip
} from '../interfaces';
import { find, includes, isEmpty } from '../utils';
import { FALLBACK_REGION, FUNCTION_DISABLED } from '../constants';

export interface AuthModelExtension {
    user?: object;
    module?: object;
    suiteModule?: object;
    navigationModule?: object;
    tenant?: object;
    member?: object;
    platformVip?: object;
    suiteVip?: object;
}

export class BaseAuthModel<
    T extends AuthModelExtension = {},
    TTimezoneKey extends string = TIMEZONE_KEY
> {
    protected user?: CurrentUser & T['user'];
    protected tenant?: CurrentTenant & T['tenant'];
    protected member?: CurrentMember & T['member'];
    protected platformVip?: PlatformVip & T['platformVip'];
    protected suiteVip?: SuiteVip & T['suiteVip'];
    protected suite_modules?: (SuiteConf & T['suiteModule'])[];
    protected navigation_modules?: (NavigationConf & T['navigationModule'])[];
    private _authSuiteId?: SuiteId;

    initConfig(options: LoginInfo) {
        this.user = options.user;
        this.tenant = options.tenant;
        this.member = options.member;
        this.platformVip = options.platformPlan;
        this.suiteVip = options.vip;
        this.suite_modules = options.suite_modules;
        this.navigation_modules = options.navigation_modules;
        this._authSuiteId = options.auth_suite_id;
    }

    get navigationModules() {
        return this.navigation_modules;
    }

    get suiteModules() {
        return this.suite_modules;
    }

    /** 标识用户是从哪个子产品进入的 */
    get authSuiteId() {
        return this._authSuiteId;
    }

    /**
     * 获取用户唯一id
     * 注意，尽量不要通过判空该字段来判断当前用户是否存在，推荐使用 isUserExist
     */
    get userId() {
        return this.user?._id || '';
    }

    /** 获取用户编号 */
    get username() {
        return this.user?.username ?? '';
    }

    /** 获取用户昵称 */
    get userNickname() {
        return this.user?.nickname ?? '';
    }

    /** 获取用户注册时间 */
    get userRegTime() {
        return this.user?.reg_time;
    }

    /** 当前用户创建的租户列表 */
    get userOwnedTenantList() {
        return this.user?.owned_tenant_list ?? [];
    }

    /** 当前用户是否没有创建任何租户 */
    get isCurrentUserNoTenant() {
        return isEmpty(this.userOwnedTenantList);
    }

    /** 获取用户的 locale */
    get userLocale() {
        return this.user?.locale ?? '';
    }

    /** 获取用户手机号 */
    get userPhone() {
        return this.user?.phone ?? '';
    }

    /** 获取用户邮箱 */
    get userEmail() {
        return this.user?.email ?? '';
    }

    /** 用户手机号是否已验证 */
    get userPhoneVerified() {
        return !!this.user?.is_phone_verified;
    }

    /** 用户邮箱是否已验证 */
    get userEmailVerified() {
        return !!this.user?.is_email_verified;
    }

    /** 用户是否已设置密码 */
    get userHasPassword() {
        return !!this.user?.has_password;
    }

    /**
     * 当前租户id
     * 注意，尽量不要通过判空该字段来判断当前企业是否存在，推荐使用 isCurrentCorpExist
     */
    get tenantId() {
        return this.tenant?.id || '';
    }

    get integrateId() {
        return this.tenant?.integrate_id ?? '';
    }

    /** 租户名称 */
    get currentCorpName() {
        return this.tenant?.name || '';
    }

    /** 获取当前企业的类型 */
    get currentCorpType() {
        return this.tenant?.integrate_type;
    }

    /** 当前租户是否包含企业微信 ISV 密文集成 */
    get hasISVEncryptedIntegration() {
        return !!this.tenant?.wxwork?.open_data;
    }

    /** 当前租户是否包含企业微信明文（CP）集成渠道 */
    get hasPlainIntegration() {
        return !!this.tenant?.wxwork?.has_plaintext_channel;
    }

    /** 是否需要屏蔽企业微信 ISV 受限功能：包含 ISV 集成且无明文集成渠道 */
    get isISVEncrypted() {
        return this.hasISVEncryptedIntegration && !this.hasPlainIntegration;
    }

    /** 是否开启企业风格 */
    get corpThemeEnabled() {
        return !!this.tenant?.corp_theme?.enabled;
    }

    /** 企业风格 */
    get corpThemeColor() {
        return this.tenant?.corp_theme?.color;
    }

    /** 是否为集成模式 */
    get isIntegratedCorpType() {
        if (!this.currentCorpType) {
            return false;
        }
        return this.currentCorpType !== CorpType.Default;
    }

    /** 获取当前企业创建者 id（userId） */
    get corpOwnerId() {
        return this.tenant?.owner_id || '';
    }

    /** 获取当前企业创建者的成员 ID */
    get corpOwnerMemberId() {
        return this.tenant?.owner_member_id || '';
    }

    /** 获取系统地区 */
    get region() {
        return this.tenant?.region || FALLBACK_REGION;
    }

    /** 获取企业时区 */
    get timezone() {
        const etc = this.tenant?.timezone?.etc;
        return etc === 'None' ? undefined : (etc as TTimezoneKey | undefined);
    }

    get timezoneLabel() {
        return this.tenant?.timezone?.label;
    }

    /**
     * 获取成员在企业下的唯一id
     * 注意，尽量不要通过判空该字段来判断当前成员是否存在，推荐使用 isMemberExist
     */
    get memberId() {
        return this.member?.member_id || '';
    }

    /** 获取成员在企业下的编号 */
    get memberUsername() {
        return this.member?.username ?? '';
    }

    /** 获取成员在企业下的昵称 */
    get memberNickname() {
        return this.member?.nickname || this.user?.nickname || '';
    }

    /** 成员头像 */
    get memberAvatar() {
        return this.member?.avatar;
    }

    /** 当前成员类型 */
    get memberType() {
        return this.member?.type;
    }

    /** 判断当前成员是否为外部用户 */
    get isExternalMember() {
        return this.member?.type === MemberType.external;
    }

    /** 成员是否是当前企业创建者 */
    get isCorpOwner() {
        return !!this.member?.is_owner;
    }

    /** 是否是集成模式下的同步成员 */
    get isSyncMember() {
        return !!this.user?.is_sync;
    }

    /**
     * 是否为系统管理员 = 企业创建者 ｜ 通讯录「系统管理组」成员
     * @deprecated 使用 hasSysAdmin 代替
     */
    get isSysAdmin() {
        return !!this.member?.is_admin;
    }

    /** 是否有开放平台管理权限 = 企业创建者 ｜ 系统管理员 ｜ 开放平台管理员 */
    get hasOpenPlatformManager() {
        return !!this.member?.is_open_platform_manager;
    }

    /**
     * 是否具有系统管理员权限（企业创建者/通讯录「系统管理组」成员）
     */
    get hasSysAdmin() {
        return !!this.member?.is_admin;
    }

    /**
     * 是否为通讯录管理员 = 企业创建者 ｜ 系统管理员 ｜ 通讯录管理员
     * @deprecated 使用 hasPlatformManager 代替
     */
    get isPlatformManager() {
        return !!this.member?.is_platform_manager;
    }

    /**
     * 是否具有通讯录管理员权限（企业创建者/系统管理员/通讯录管理员）
     */
    get hasPlatformManager() {
        return !!this.member?.is_platform_manager;
    }

    /**
     * 应用搭建套件版本
     * @deprecated 不要新增使用场景
     */
    get suiteVipLevel() {
        return this.suiteVip?.pack?.level;
    }

    /** 用户是否存在 */
    get isUserExist() {
        return !isEmpty(this.user);
    }

    /** 成员是否存在 */
    get isMemberExist() {
        return !isEmpty(this.member);
    }

    /** 当前企业是否存在 */
    get isCurrentCorpExist() {
        return !isEmpty(this.tenant);
    }

    /**
     * 当前登录账号是否不在任何企业内（没有创建企业，也没有加入企业）
     * tenant为空，且user.owned_tenant_list为空，就可以认为当前用户在空企业中
     *
     * @see FXD-51872
     */
    get isCurrentUserNoCorp() {
        return !this.isCurrentCorpExist && this.isCurrentUserNoTenant;
    }

    /** 是否有通讯录内部部门管理权限 */
    get hasTeamWritePermission() {
        return !!this.member?.has_team_write_permission;
    }

    /**
     * 是否有通讯录内部部门查看权限
     * 后端未返回部门查看权限时沿用部门管理权限。
     */
    get hasTeamReadPermission() {
        return !!(this.member?.has_team_read_permission ?? this.member?.has_team_write_permission);
    }

    /** 是否有通讯录内部角色可见权限 */
    get hasRolePermission() {
        return !!this.member?.has_role_read_permission;
    }

    /** 是否有通讯录内部组织 TAB 可见权限 */
    get hasContactInternalPermission() {
        return this.hasRolePermission || this.hasTeamReadPermission;
    }

    /** 是否有互联组织管理权限 */
    get hasCorpCoopManagePermission() {
        return !!this.member?.has_corp_coop_manage_permission;
    }

    /** 是否有外部成员管理权限 */
    get hasExternalMemberManagePermission() {
        return !!this.member?.has_external_member_manage_permission;
    }

    /** 是否有外部角色管理权限 */
    get hasExternalRoleManagePermission() {
        return !!this.member?.has_external_role_manage_permission;
    }

    /** 是否有外部用户 TAB 可见权限 */
    get hasContactExternalPermission() {
        return this.hasExternalMemberManagePermission || this.hasExternalRoleManagePermission;
    }

    /** 是否有通讯录分组可见权限 */
    get hasContactPermission() {
        return (
            this.hasContactInternalPermission ||
            this.hasContactExternalPermission ||
            this.hasCorpCoopManagePermission
        );
    }

    /** 获取用户登录类型 */
    get userLoginType() {
        return this.user?.login_type;
    }

    /**
     * JSY 迁移用户id
     */
    get jsyUserId() {
        return this.user?.jsy_user_id;
    }

    /** 水印配置 */
    get watermark() {
        return this.tenant?.watermark;
    }

    /** 获取代管企业名称 */
    get tmpTenantName() {
        return this.tenant?.tmp_tenant?.name ?? '';
    }

    /** 平台版本 */
    get platformVipLevel() {
        return this.platformVip?.plan;
    }

    /** 平台付费功能点 */
    get platformVipPack() {
        return this.platformVip?.pack;
    }

    /**
     * 是否为平台免费版（免费+试用）
     * 高级功能均提示升级
     */
    get isPlatformFreeVersion() {
        return includes([PlatformVipLevel.free, PlatformVipLevel.trial], this.platformVipLevel);
    }

    get hasTemplateCenter() {
        return this.member?.has_template_center;
    }

    /** 是否为钉钉集成模式 */
    get isDingTalkCorp() {
        return this.currentCorpType === CorpType.DingTalk;
    }

    /** 获取当前企业的规模 */
    get corpSize() {
        return this.tenant?.size;
    }

    getNavigationModuleByKey(key: string) {
        return find(this.navigation_modules, ['key', key]);
    }

    getSuiteModuleById(suiteId: SuiteId) {
        return find(this.suite_modules, ['suiteId', suiteId]);
    }

    isSuiteEnabled(suiteId: SuiteId) {
        return !!this.getSuiteModuleById(suiteId)?.enable;
    }

    /** 判断指定租户是否由当前用户创建 */
    isOwnedTenant(tenantId: string) {
        return this.userOwnedTenantList.some((tenant) => tenant.id === tenantId);
    }

    /** 平台功能是否允许增购 */
    allowAddonInPlatform(key: PlatformVipAddOnKey) {
        return includes(this.platformVip?.upgradable_addons, key);
    }

    /**
     * 平台套餐是否包含指定功能
     * @param key PlatformVipAddOnKey
     */
    hasPlatformVipFunction(key: PlatformVipAddOnKey) {
        return (this.platformVip?.pack[key] ?? FUNCTION_DISABLED) !== FUNCTION_DISABLED;
    }

    /** 是否可以访问指定套件 */
    isSuiteAccessible(suiteId: number) {
        return includes(this.member?.visible_suite_ids, suiteId);
    }

    /**
     * 指定租户模块是否开启
     */
    isTenantModuleEnabled(key: string) {
        return this.tenant?.modules?.[key] ?? false;
    }
}
